import * as ApiType from '@/types/ApiTypes';
import { UIModel } from './UIModel';
import { BehaviorSubject } from 'rxjs';
import { API } from './Api';

export abstract class Manager<T extends ApiType.APIModel, U> {
  abstract API: string;
  data = new BehaviorSubject<U[]>([]);
  totalCount = 0;
  totalPageCount = 0;
  currentPage = 0;
  itemsPerPage = 10;
  args: any;

  abstract convert(apiModel: T): Promise<U>;

  async load(args: any = {}): Promise<void> {
    this.args = args;
    const skip = 0;
    const limit = this.itemsPerPage;
    const [countRes, queryRes] = await Promise.all([
      API.get<{ total: number }>(`${this.API}count/`, args),
      API.get<T[]>(this.API, { ...args, _skip: skip, _limit: limit }),
    ]);
    if (countRes.success && queryRes.success) {
      this.totalCount = countRes.body!.total;
      this.totalPageCount = Math.ceil(this.totalCount / this.itemsPerPage);
      this.currentPage = skip / this.itemsPerPage + 1;
      const nextData = await Promise.all(queryRes.body!.map((item) => this.convert(item)));
      this.data.next(nextData);
      return Promise.resolve();
    } else {
      return Promise.reject(countRes.detail ?? queryRes.detail);
    }
  }

  async loadMore() {
    if (this.currentPage < this.totalPageCount) {
      this.currentPage++;
      const skip = (this.currentPage - 1) * this.itemsPerPage;
      const limit = this.itemsPerPage;
      const res = await API.get<T[]>(this.API, { ...this.args, _skip: skip, _limit: limit });
      if (res.success) {
        const nextData = await Promise.all(res.body!.map((item) => this.convert(item)));
        this.data.next([...this.data.value, ...nextData]);
        return Promise.resolve();
      } else {
        this.currentPage--;
        return Promise.reject(res.detail);
      }
    }
  }

  async loadAll(args: any = {}) {
    await this.load(args);
    while (this.hasMore()) {
      await this.loadMore();
    }
    return Promise.resolve();
  }

  hasMore(): boolean {
    return this.currentPage < this.totalPageCount;
  }

  reset(): void {
    this.totalCount = 0;
    this.totalPageCount = 0;
    this.currentPage = 0;
    this.data.next([]);
    this.args = undefined;
  }

  async retrieve(id: number): Promise<U> {
    const res = await API.get<T>(`${this.API}${id}/`);
    if (res.success) {
      return this.convert(res.body!);
    } else {
      return Promise.reject(res.detail);
    }
  }

  async create(model: T): Promise<U> {
    const res = await API.post<T>(this.API, model);
    if (res.success) {
      return this.convert(res.body!);
    } else {
      return Promise.reject(res.detail);
    }
  }

  async update(model: T): Promise<U> {
    const res = await API.put<T>(`${this.API}${model.id}/`, model);
    if (res.success) {
      return this.convert(res.body!);
    } else {
      return Promise.reject(res.detail);
    }
  }

  async patch(model: Partial<T> & { id: number }): Promise<U> {
    const res = await API.patch<T>(`${this.API}${model.id}/`, model);
    if (res.success) {
      return this.convert(res.body!);
    } else {
      return Promise.reject(res.detail);
    }
  }

  async delete(id: number): Promise<void> {
    const res = await API.delete(`${this.API}${id}/`);
    if (res.success) {
      return Promise.resolve();
    } else {
      return Promise.reject(res.detail);
    }
  }

  async upload(id: number, field: string, file: File): Promise<U> {
    const data = new FormData();
    data.append('file', file);
    data.append('field', field);
    const res = await API.upload<T>(`${this.API}upload/${id}/`, data);
    if (res.success) {
      return this.convert(res.body!);
    } else {
      return Promise.reject(res.detail);
    }
  }
}

export abstract class PureManager<T extends ApiType.APIModel> extends Manager<T, T> {
  convert(apiModel: T): Promise<T> {
    return Promise.resolve(apiModel);
  }
}
